import React, { useState } from 'react';
import {
  useDeleteV2ProviderDocumentsPromptsByPromptIdMutation,
  useGetV2ProviderDocumentsPromptsQuery,
} from '@features/markdown-editor-ai/store/api';
import type { CustomPromptResponse } from '@features/markdown-editor-ai/types';
import {
  AccessTime as TimeIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useSnackbar } from 'notistack';

interface CustomTemplateSelectorProps {
  onSelectTemplate: (template: CustomPromptResponse) => void;
  disabled?: boolean;
}

export const CustomTemplateSelector: React.FC<CustomTemplateSelectorProps> = ({
  onSelectTemplate,
  disabled = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedTemplate, setSelectedTemplate] = useState<CustomPromptResponse | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState<CustomPromptResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isLoading, refetch } = useGetV2ProviderDocumentsPromptsQuery();
  const [deleteTemplate] = useDeleteV2ProviderDocumentsPromptsByPromptIdMutation();

  const templates = data?.prompts || [];

  const handleDeleteClick = (
    event: React.MouseEvent,
    template: CustomPromptResponse,
  ) => {
    event.stopPropagation();
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!templateToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTemplate({ promptId: templateToDelete.prompt_id }).unwrap();
      enqueueSnackbar('Template deleted successfully', { variant: 'success' });
      setDeleteDialogOpen(false);
      setTemplateToDelete(null);
      refetch();
    } catch (error) {
      console.error('Failed to delete template:', error);
      enqueueSnackbar('Failed to delete template', { variant: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleTemplateSelect = (template: CustomPromptResponse | null) => {
    if (template) {
      setSelectedTemplate(template);
      onSelectTemplate(template);
    }
  };

  if (isLoading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" p={2}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="text.secondary" ml={2}>
          Loading templates...
        </Typography>
      </Box>
    );
  }

  if (templates.length === 0) {
    return null; // Don't show selector if no templates
  }

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Or use one of your templates:
        </Typography>
        <Autocomplete
          options={templates}
          value={selectedTemplate}
          onChange={(_, value) => handleTemplateSelect(value)}
          disabled={disabled}
          getOptionLabel={(option) => option.name}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select a template..."
              size="small"
            />
          )}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            return (
              <Box
                key={key}
                component="li"
                {...otherProps}
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  py: 1.5,
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <DocumentIcon fontSize="small" color="primary" />
                  <Typography variant="body1" fontWeight={500}>
                    {option.name}
                  </Typography>
                </Box>
                {option.description && (
                  <Typography 
                    variant="body2" 
                    color="text.secondary" 
                    sx={{ 
                      mt: 0.5,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {option.description}
                  </Typography>
                )}
                <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                  <TimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <Typography variant="caption" color="text.disabled">
                    Created {format(new Date(option.created_at), 'MMM d, yyyy')}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                size="small"
                onClick={(e) => handleDeleteClick(e, option)}
                sx={{
                  ml: 1,
                  opacity: 0.7,
                  '&:hover': {
                    opacity: 1,
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
            );
          }}
          PaperComponent={(props) => (
            <Paper {...props} elevation={8} sx={{ maxHeight: 400, overflow: 'auto' }} />
          )}
        />
        {selectedTemplate && (
          <Box sx={{ mt: 1.5, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Selected template:
            </Typography>
            <Typography variant="body2" fontWeight={500}>
              {selectedTemplate.name}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => !isDeleting && setDeleteDialogOpen(false)}
      >
        <DialogTitle>Delete Template</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the template &quot;{templateToDelete?.name}&quot;? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};